// @ts-strict-ignore
import type { Draft } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import { getProperty, setProperty } from 'dot-prop';
import * as jsonDiffPatch from 'jsondiffpatch';
import buildDependencies from '../engine/dependencies';
import { normalizeExpressionFunctionSeparators } from '../engine/utils';

import type { WorkflowState } from './state';
import { ActionType } from '../engine/constants';
import {
  getTagActionsByOutcomeReason,
  isActionOfType,
} from '../../service/actions';
import type {
  Action,
  Dependency,
  Expression,
  ExpressionTerm,
  PublishedAttribute,
  SchemaInstance,
  Tag,
  WorkflowInfo,
  WorkflowProps,
} from '../engine/types';
import { WEBHOOK_KEY } from 'flow/service/actionOptions';
import type { WorkflowSnapshots } from './types';
import { createSnapshot } from 'flow/service/history';
import { isOutcomeEphemeral } from 'flow/service/outcomes';
import type { WorkflowPolicy } from 'flow/service/policy';

function getWorkflowsByApplicationId(allWorkflows: WorkflowInfo[]) {
  return allWorkflows.reduce((all, workflow) => ({
    ...all,
    [workflow.application_id]: workflow,
  }), {} as Record<string, WorkflowInfo>);
}

// remove configurations that are not available to the current customer, as imports can occur across customers
export function sanitizeImportedActions(
  importedActions: Record<string, Action>,
  allCustomerWorkflows: WorkflowInfo[],
) {
  const sanitizedActions: Record<string, Action> = {};
  const allWorkflowsByAppId = getWorkflowsByApplicationId(allCustomerWorkflows);

  Object.entries(importedActions).forEach(([actionId, action]) => {
    sanitizedActions[actionId] = structuredClone(action);

    if (isActionOfType(action, ActionType.SERVICE)) {
      // overwrite the service options object to ensure that we
      // do not add invalid recurring workflow configurations
      sanitizedActions[actionId].options = {};

      Object.entries(importedActions[actionId].options).forEach(([optionId, option]) => {
        if (optionId === WEBHOOK_KEY) {
          const { value } = option;

          if (allWorkflowsByAppId[Number(value)]) {
            sanitizedActions[actionId].options[WEBHOOK_KEY] = structuredClone(option);
          }
        } else {
          sanitizedActions[actionId].options[optionId] = structuredClone(option);
        }
      });
    }
  });
  return sanitizedActions;
}

export function snapshotAndCloneWorkflowData(state: WorkflowState) {
  const workflowData = cloneDeep(state.workflowData);
  const workflowSnapshots: WorkflowSnapshots = {
    ...state.workflowSnapshots,
    workflowData: {
      history: {
        initial: state.workflowSnapshots.workflowData.history.initial,
        past: [
          // Only store the last 100 snapshots for performance reasons
          ...state.workflowSnapshots.workflowData.history.past.slice(-100),
          createSnapshot({ data: cloneDeep(state.workflowData) }),
        ],
      },
    },
  };

  return { workflowData, workflowSnapshots };
}


/**
 * Every reducer that writes an expression ends here, so min/max operators are repaired in one place
 * rather than in each reducer. Loading a workflow does not come through here, so opening one never
 * rewrites it.
 */
function normalizeOutputExpressions(actions: WorkflowState['workflowData']['actions']) {
  return Object.entries(actions ?? {}).reduce((result, [publicId, action]) => {
    const expression = action?.output?.expression;

    if (!expression) return result;

    return setProperty(
      result,
      `${publicId}.output.expression`,
      normalizeExpressionFunctionSeparators(expression),
    );
  }, actions ?? {});
}

export function compileWorkflowDataDependencies(workflowData: WorkflowState['workflowData']) {
  const actions = normalizeOutputExpressions(workflowData.actions);

  return {
    ...workflowData,
    actions,
    dependencies: buildDependencies({
      actions,
      dataRoutes: workflowData.dataRoutes,
      thresholds: workflowData.thresholds,
    }),
  };
}

/**
 * Get parent expression item by generating a new path that excludes the last index in the original path string
 */
export function getParentPathAndExpressionItem({
  indices,
  existingExpression,
}: {
  indices: string[];
  existingExpression: Expression;
}): {
    pathWithoutLastIndex: string;
    parentExpressionArrayOfTheExpressionOnTheLastIndex: (ExpressionTerm | ExpressionTerm[])[];
  } {
  if (indices.length === 1) {
    return {
      pathWithoutLastIndex: '',
      parentExpressionArrayOfTheExpressionOnTheLastIndex: [],
    };
  }

  // e.g. original path: [0][1] -> new path: [0]
  const pathWithoutLastIndex = indices.slice(0, Number(indices.length - 1))
    .reduce((result, indexValue) => `${result}[${indexValue}]`, '');
  // we get the parent expression array using that new path
  const parentExpressionArrayOfTheExpressionOnTheLastIndex = getProperty(
    existingExpression, pathWithoutLastIndex) as ExpressionTerm[];

  return {
    pathWithoutLastIndex,
    parentExpressionArrayOfTheExpressionOnTheLastIndex,
  };
}

export function resetPendingGroup(state: WorkflowState) {
  state.ui.expressionBuilder.pendingGroup = { path: null };
}

export function removeEphemeralOutcomeFromTags({
  actions,
  tags,
  outcomeName,
}: {
  actions: Partial<SchemaInstance>['actions'];
  tags: Tag[];
  outcomeName: string;
}): {
    updatedActions: Partial<SchemaInstance>['actions'];
    updatedTags: Tag[];
  } {
  const relatedTagOutcomeActions = getTagActionsByOutcomeReason(actions, outcomeName);

  if (relatedTagOutcomeActions.length === 0) {
    return {
      updatedActions: actions,
      updatedTags: tags,
    };
  }

  const updatedActions = { ...actions };
  const updatedTags = [...tags];

  relatedTagOutcomeActions.forEach(tagAction => {
    const updatedTagAction = {
      ...tagAction,
      tag: {
        ...tagAction.tag,
        outcomes: tagAction.tag.outcomes
          .filter(outcome => !(outcome.isEphemeral && outcome.outcome === outcomeName)),
      },
    };

    const matchingTagProp = tags
      .find(tag => tag.tag_name === tagAction.tag.name);

    if (matchingTagProp) {
      const updatedTagProp = {
        ...matchingTagProp,
        outcomes: matchingTagProp.outcomes
          .filter(outcome => !(isOutcomeEphemeral(outcome)
        && outcome.outcome === outcomeName)),
      };

      updatedActions[tagAction.publicId] = updatedTagAction;
      const tagIndex = updatedTags.findIndex(tag => tag.tag_name === matchingTagProp.tag_name);
      updatedTags[tagIndex] = updatedTagProp;
    }
  });


  return { updatedActions, updatedTags };
}
/**
 * This is unused for now but will be once we support all account-level elements that
 * update an Action
 * todo BUILD-817
 */
export function computeActionsToUpdateWithNewProps(
  actions: WorkflowState['workflowData']['actions'],
  previousProps: WorkflowProps,
  nextProps: Partial<WorkflowProps>,
) {
  const nextActions = cloneDeep(actions);

  const previousPAs = previousProps.publishedAttributes;
  const nextPAs = nextProps.publishedAttributes;
  // only PAs are supported for now - the diffing will grow to support tags, outcomes, and services
  const diffPatcher = jsonDiffPatch.create({
    objectHash: (pa: PublishedAttribute) => pa.attributeToken,
  });

  const diffResult = diffPatcher.diff(previousPAs, nextPAs);

  const diffByPAToken = Object
    .entries(diffResult)
    .reduce((result, [key, changes]) => {
      const numericKey = Number(key);

      if (!isNaN(numericKey)) {
        const paToken = nextPAs[numericKey].attributeToken;
        result[paToken] = changes;
      }
      return result;
    }, {});


  // maps the incoming PA field names to the equivalent action name
  const publishedAttributePropertyMap = {
    attributeName: 'name',
    dataType: 'dataType',
  };

  Object.entries(diffByPAToken).forEach(([token, changes]) => {
    /**
     * todo - once other elements (tags, etc) are supported, actions should indexed via either
     * their token or some other identifier to avoid this nested loop.
     */
    const action = Object.values(nextActions)
      .find(a => a.input?.publishedAttributeToken === token);

    Object
      .entries(changes)
      .forEach(([field, values]) => {
        if (Object.hasOwn(publishedAttributePropertyMap, field)) {
          // diff changes are in a 2-element array with the old and new value
          const [, newValue] = values;
          const actionKeyName = publishedAttributePropertyMap[field];
          action.input[actionKeyName] = newValue;
        }
      });
  });


  return nextActions;
}

type MergeUniqueValuesProp<T> = {
  initial: Record<string, T>;
  current: Record<string, T>;
};

/**
 * Combines the initial and current snapshot of key-value pairs by taking the union
 * based on the key of each pair, removing duplicate values of each key.
 */
export function mergeUniqueValues<T>({
  initial,
  current,
}: MergeUniqueValuesProp<T>) {
  const combined: Record<string, T> = {};
  const allKeys = new Set([...Object.keys(initial ?? {}), ...Object.keys(current ?? {})]);
  for (const key of allKeys) {
    combined[key] = current[key] ?? initial[key];
  }
  return combined;
}

type MergeDependencies = {
  initial: Record<string, Dependency[]>;
  current: Record<string, Dependency[]>;
};

/**
 * Combines the initial and current snapshot of key-value[] pairs of dependencies by unioning
 * based off of the dependency id, type, and preview state, removing duplicate dependencies under
 * overlapping conditions, and prevents arbitrary overwriting by dependencies that are preview-
 * only (which buildGraph would remove otherwise, causing bugs on the workflow canvas).
 */
export function mergeDependencies({
  initial,
  current,
}: MergeDependencies): Record<string, Dependency[]> {
  const combined: Record<string, Dependency[]> = {};
  const allKeys = new Set([...Object.keys(initial ?? {}), ...Object.keys(current ?? {})]);

  for (const key of allKeys) {
    const uniqueDependencies = new Map<string, Dependency>();
    for (const dependency of [...(initial[key] ?? []), ...(current[key] ?? [])]) {
      const dependencyKey = JSON.stringify({
        id: dependency.id,
        type: dependency.type,
        isPreview: dependency.isPreview ?? false,
      });
      uniqueDependencies.set(dependencyKey, dependency);
    }
    combined[key] = [...uniqueDependencies.values()];
  }

  return combined;
}


export function createWorkflowPolicyFromState(
  state: WorkflowState,
): WorkflowPolicy {
  return {
    data: {
      actions: state.workflowData.actions,
      thresholds: state.workflowData.thresholds,
      dataRoutes: state.workflowData.dataRoutes,
    },
    props: state.workflowProps,
    dependencies: state.workflowData.dependencies,
  };
}

export function applyPolicyToState(
  policy: WorkflowPolicy,
  state: Draft<WorkflowState>,
): void {
  state.workflowData.actions = policy.data.actions;
  state.workflowData.thresholds = policy.data.thresholds;
  state.workflowData.dataRoutes = policy.data.dataRoutes;
  state.workflowData.dependencies = policy.dependencies;
  state.workflowProps = policy.props;
}
